import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Search, ShieldAlert, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { AdminAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFormatters } from "@/lib/format";

function StatusBadge({ status }) {
    if (status === "suspended") {
        return (
            <Badge
                data-testid="tenant-status-suspended"
                className="border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-100"
            >
                Suspended
            </Badge>
        );
    }
    return (
        <Badge
            data-testid="tenant-status-active"
            className="border-emerald-300 bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
        >
            Active
        </Badge>
    );
}

function CreateTenantDialog({ open, onOpenChange, onCreated }) {
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        branch_name: "Main Garage",
        branch_code: "HQ",
        branch_state_code: "KA",
        currency: "INR",
        owner_email: "",
        owner_password: "",
        owner_name: "",
    });
    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        if (form.owner_password.length < 8) {
            toast.error("Owner password must be ≥ 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            const r = await AdminAPI.createTenant(form);
            toast.success(`Created tenant: ${r.tenant.name}`);
            onCreated?.(r.tenant);
            onOpenChange(false);
            setForm((f) => ({ ...f, name: "", owner_email: "", owner_password: "", owner_name: "" }));
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="create-tenant-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Create a new tenant</DialogTitle>
                    <DialogDescription>
                        Provisions a tenant, a default branch, and an initial owner user.
                        Owner can log in immediately.
                    </DialogDescription>
                </DialogHeader>
                <form className="grid gap-3" onSubmit={submit}>
                    <div className="grid gap-1.5">
                        <Label htmlFor="ct-name" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                            Tenant name
                        </Label>
                        <Input
                            id="ct-name" required value={form.name} onChange={update("name")}
                            data-testid="ct-name" placeholder="Acme Auto Care"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1.5">
                            <Label htmlFor="ct-branch-name" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                                Branch name
                            </Label>
                            <Input id="ct-branch-name" required value={form.branch_name} onChange={update("branch_name")}
                                    data-testid="ct-branch-name" />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="ct-branch-code" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                                Branch code
                            </Label>
                            <Input id="ct-branch-code" required value={form.branch_code} onChange={update("branch_code")}
                                    data-testid="ct-branch-code" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1.5">
                            <Label htmlFor="ct-state" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                                State code (GST)
                            </Label>
                            <Input id="ct-state" required value={form.branch_state_code} onChange={update("branch_state_code")}
                                    data-testid="ct-state" />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="ct-currency" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                                Currency
                            </Label>
                            <Input id="ct-currency" required value={form.currency} onChange={update("currency")}
                                    data-testid="ct-currency" maxLength={3} />
                        </div>
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="ct-owner-name" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                            Owner display name
                        </Label>
                        <Input id="ct-owner-name" required value={form.owner_name} onChange={update("owner_name")}
                                data-testid="ct-owner-name" placeholder="Owner full name" />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="ct-owner-email" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                            Owner email
                        </Label>
                        <Input id="ct-owner-email" type="email" required autoComplete="off"
                                value={form.owner_email} onChange={update("owner_email")}
                                data-testid="ct-owner-email" placeholder="owner@workshop.example" />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="ct-owner-password" className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                            Owner password (≥ 8 chars)
                        </Label>
                        <Input id="ct-owner-password" type="password" required minLength={8} autoComplete="new-password"
                                value={form.owner_password} onChange={update("owner_password")}
                                data-testid="ct-owner-password" />
                    </div>
                    <DialogFooter className="mt-2">
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="ct-cancel">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={submitting} data-testid="ct-submit"
                                 className="bg-brand-red text-white hover:bg-brand-redDeep">
                            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Create tenant
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ImpersonateConfirm({ tenant, open, onOpenChange, onConfirm, pending }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="impersonate-confirm-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-700">
                        <ShieldAlert className="h-5 w-5" />
                        Confirm impersonation
                    </DialogTitle>
                    <DialogDescription>
                        You are about to impersonate <strong>{tenant?.name}</strong>. You will be
                        signed in as the tenant owner for the next 15 minutes. All actions are
                        logged to the platform audit log.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="imp-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={pending}
                        data-testid="imp-confirm"
                        className="bg-red-600 text-white hover:bg-red-700"
                    >
                        {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Yes, impersonate
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminTenantsPage() {
    const { startImpersonation } = useAuth();
    const { formatDate } = useFormatters();
    const nav = useNavigate();

    const [q, setQ] = useState("");
    const [debounced, setDebounced] = useState("");
    const [page, setPage] = useState(1);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [impTarget, setImpTarget] = useState(null);
    const [impPending, setImpPending] = useState(false);

    useEffect(() => {
        const id = setTimeout(() => setDebounced(q.trim()), 300);
        return () => clearTimeout(id);
    }, [q]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await AdminAPI.listTenants({ q: debounced || undefined, page, page_size: 20 });
            setItems(r.items || []);
            setTotal(r.total || 0);
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setLoading(false);
        }
    }, [debounced, page]);

    useEffect(() => { load(); }, [load]);

    async function toggleStatus(t) {
        const next = t.status === "suspended" ? "active" : "suspended";
        try {
            await AdminAPI.updateTenant(t.id, { status: next });
            toast.success(`${t.name} is now ${next}`);
            load();
        } catch (err) {
            toast.error(extractError(err));
        }
    }

    async function confirmImpersonation() {
        if (!impTarget) return;
        setImpPending(true);
        const r = await startImpersonation(impTarget.id);
        setImpPending(false);
        if (r.ok) {
            toast.success(`Impersonating ${impTarget.name}`);
            setImpTarget(null);
            nav("/", { replace: true });
        } else {
            toast.error(r.message || "Impersonation failed");
        }
    }

    const pageCount = Math.max(1, Math.ceil(total / 20));

    return (
        <div className="space-y-6" data-testid="admin-tenants-page">
            <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        Platform · Tenants
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        Tenants
                    </h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        Every workshop running on AutoBrix. Click a row for detail, or impersonate
                        an owner from here.
                    </p>
                </div>
                <Button
                    onClick={() => setCreateOpen(true)}
                    data-testid="new-tenant-btn"
                    className="bg-brand-red text-white hover:bg-brand-redDeep"
                >
                    <Plus className="mr-1 h-4 w-4" />
                    New tenant
                </Button>
            </header>

            <Card className="flex items-center gap-2 p-3">
                <Search className="ml-1 h-4 w-4 text-zinc-400" />
                <Input
                    value={q}
                    onChange={(e) => { setPage(1); setQ(e.target.value); }}
                    placeholder="Search name or slug…"
                    data-testid="tenants-search"
                    className="border-0 shadow-none focus-visible:ring-0"
                />
                <span className="text-xs text-zinc-500" data-testid="tenants-total">
                    {loading ? "…" : `${total} tenants`}
                </span>
            </Card>

            <Card className="overflow-hidden" data-testid="tenants-table-card">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="text-right">Users</TableHead>
                            <TableHead className="text-right">Active jobs</TableHead>
                            <TableHead className="text-right">Invoices (30d)</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-zinc-500">No tenants match this filter.</TableCell></TableRow>
                        ) : (
                            items.map((t) => (
                                <TableRow key={t.id} data-testid={`tenant-row-${t.slug}`}>
                                    <TableCell className="font-medium">
                                        <Link to={`/admin/tenants/${t.id}`} className="hover:underline">
                                            {t.name}
                                        </Link>
                                        <div className="font-mono text-[11px] text-zinc-500">{t.slug}</div>
                                    </TableCell>
                                    <TableCell className="text-sm text-zinc-600 dark:text-zinc-400">
                                        {t.created_at ? formatDate(t.created_at) : "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">{t.user_count}</TableCell>
                                    <TableCell className="text-right tabular-nums">{t.active_jobcards_count}</TableCell>
                                    <TableCell className="text-right tabular-nums">{t.total_invoices_30d}</TableCell>
                                    <TableCell><StatusBadge status={t.status} /></TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button
                                                size="sm" variant="outline"
                                                onClick={() => setImpTarget(t)}
                                                data-testid={`impersonate-btn-${t.slug}`}
                                                className="h-8"
                                            >
                                                <ShieldAlert className="mr-1 h-3.5 w-3.5" />
                                                Impersonate
                                            </Button>
                                            <Button
                                                size="sm" variant="ghost"
                                                onClick={() => toggleStatus(t)}
                                                data-testid={`toggle-status-${t.slug}`}
                                                className="h-8"
                                            >
                                                {t.status === "suspended" ? (
                                                    <><ShieldCheck className="mr-1 h-3.5 w-3.5" />Activate</>
                                                ) : (
                                                    <>Suspend</>
                                                )}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            {pageCount > 1 && (
                <div className="flex items-center justify-between text-sm text-zinc-500">
                    <span>Page {page} of {pageCount}</span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                data-testid="tenants-page-prev">
                            Previous
                        </Button>
                        <Button variant="outline" size="sm" disabled={page >= pageCount}
                                onClick={() => setPage((p) => p + 1)}
                                data-testid="tenants-page-next">
                            Next
                        </Button>
                    </div>
                </div>
            )}

            <CreateTenantDialog
                open={createOpen} onOpenChange={setCreateOpen}
                onCreated={() => load()}
            />
            <ImpersonateConfirm
                tenant={impTarget}
                open={!!impTarget}
                onOpenChange={(o) => !o && setImpTarget(null)}
                onConfirm={confirmImpersonation}
                pending={impPending}
            />
        </div>
    );
}
